import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { navigateWithState } from '../utils/navigationState';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Star, MessageSquare, Trash2, Tag, StickyNote, Search, Filter, Send, CheckSquare, Square, Building2, Handshake, Plus, X, Users, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import ViewResourceProfileButton from '../components/ViewResourceProfileButton';
import BulkContactBar from '../components/BulkContactBar';
import PreReservationModal from '../components/PreReservationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FavoritesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [filteredFavorites, setFilteredFavorites] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [editingNotes, setEditingNotes] = useState(null);
  const [editingTags, setEditingTags] = useState(null);
  const [myProjects, setMyProjects] = useState([]);
  const [showProjectSelector, setShowProjectSelector] = useState(null);
  
  // Bulk selection
  const [selectedResources, setSelectedResources] = useState([]);
  
  // Pre-reservation modal
  const [showPreReservationModal, setShowPreReservationModal] = useState(null);
  
  // RM-specific states
  const [favoriteProjects, setFavoriteProjects] = useState([]);
  const [favoriteCompanies, setFavoriteCompanies] = useState([]);
  
  // Business Partners
  const [businessPartners, setBusinessPartners] = useState([]);
  const [showAddPartnerModal, setShowAddPartnerModal] = useState(false);
  const [partnerSearchTerm, setPartnerSearchTerm] = useState('');
  const [searchedCompanies, setSearchedCompanies] = useState([]);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [manualPartnerName, setManualPartnerName] = useState('');
  const [manualPartnerEmail, setManualPartnerEmail] = useState('');
  const [partnerNotes, setPartnerNotes] = useState('');
  
  // Collapsible sections state - Default to collapsed for cleaner UX
  const [expandedSections, setExpandedSections] = useState({
    resources: false,
    projects: false,
    companies: false,
    partners: false
  });
  
  // Global search for all sections
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  
  // Get user from AuthContext
  const { user } = useContext(AuthContext);
  const isRM = user?.role === 'resource_manager' || user?.role === 'operating_partner';
  
  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    fetchFavorites();
    fetchMyProjects();
    fetchBusinessPartners();
  }, [sortBy]);

  // Restore state when returning from navigation
  useEffect(() => {
    if (location.state?.restoreState) {
      if (location.state.searchTerm) setSearchTerm(location.state.searchTerm);
      if (location.state.filterTag) setFilterTag(location.state.filterTag);
      if (location.state.sortBy) setSortBy(location.state.sortBy);
      
      // Restore scroll position with longer delay for content to load
      if (location.state.scrollPosition) {
        setTimeout(() => {
          window.scrollTo({
            top: location.state.scrollPosition,
            behavior: 'instant'
          });
        }, 500); // Longer delay for favorites to load
      }
      
      // Clear the location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    applyFilters();
  }, [favorites, searchTerm, filterTag]);
  
  // Fetch Business Partners
  const fetchBusinessPartners = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/business-partners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessPartners(response.data || []);
    } catch (error) {
      console.log('Could not fetch business partners:', error.message);
      setBusinessPartners([]);
    }
  };

  // Search companies for partner modal
  const searchCompaniesForPartner = async (term) => {
    if (!term || term.length < 3) {
      setSearchedCompanies([]);
      return;
    }
    
    const token = localStorage.getItem('token');
    setSearchingCompanies(true);
    
    try {
      const response = await axios.get(`${API}/companies/search?q=${encodeURIComponent(term)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchedCompanies(response.data?.companies || response.data || []);
    } catch (error) {
      console.log('Could not search companies:', error.message);
      setSearchedCompanies([]);
    } finally {
      setSearchingCompanies(false);
    }
  };

  // Add Business Partner
  const addBusinessPartner = async (companyData) => {
    const token = localStorage.getItem('token');
    
    try {
      const payload = {
        company_id: companyData.id || null,
        company_name: companyData.name || companyData.company_name || manualPartnerName,
        company_email: companyData.contact_email || manualPartnerEmail,
        notes: partnerNotes
      };
      
      await axios.post(`${API}/business-partners`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Business partner added');
      fetchBusinessPartners();
      setShowAddPartnerModal(false);
      resetPartnerForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add partner');
    }
  };

  // Remove Business Partner
  const removeBusinessPartner = async (partnerId) => {
    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/business-partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Business partner removed');
      fetchBusinessPartners();
    } catch (error) {
      toast.error('Failed to remove partner');
    }
  };

  const resetPartnerForm = () => {
    setPartnerSearchTerm('');
    setSearchedCompanies([]);
    setManualPartnerName('');
    setManualPartnerEmail('');
    setPartnerNotes('');
  };
  
  // Reload favorites when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchFavorites();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const fetchFavorites = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      if (isRM) {
        // RM: Fetch favorite projects and companies
        const [projectsRes, companiesRes] = await Promise.all([
          axios.get(`${API}/favorites/projects`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] })),
          axios.get(`${API}/favorites/companies`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] }))
        ]);
        setFavoriteProjects(projectsRes.data || []);
        setFavoriteCompanies(companiesRes.data || []);
      } else {
        // PM: Fetch favorite resources AND companies
        const [resourcesRes, companiesRes] = await Promise.all([
          axios.get(`${API}/favorites/resources/detailed`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { sort_by: sortBy }
          }).catch(() => ({ data: [] })),
          axios.get(`${API}/favorites/companies`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] }))
        ]);
        
        setFavorites(resourcesRes.data || []);
        setFavoriteCompanies(companiesRes.data || []);
        
        // Extract all unique tags
        const tags = new Set();
        (resourcesRes.data || []).forEach(fav => {
          fav.favorite.tags?.forEach(tag => tags.add(tag));
        });
        setAllTags(Array.from(tags));
      }
    } catch (error) {
      toast.error('Failed to load favorites');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...favorites];

    // Search filter - with null safety
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(fav => {
        if (!fav.resource) return false;
        return (
          fav.resource.name?.toLowerCase().includes(term) ||
          fav.resource.role?.toLowerCase().includes(term) ||
          fav.resource.city?.toLowerCase().includes(term) ||
          fav.resource.country?.toLowerCase().includes(term) ||
          fav.company?.name?.toLowerCase().includes(term)
        );
      });
    }

    // Tag filter
    if (filterTag) {
      filtered = filtered.filter(fav =>
        fav.favorite?.tags?.includes(filterTag)
      );
    }

    setFilteredFavorites(filtered);
  };
  
  // Global filtered data for all sections (using global search)
  const filteredProjectsGlobal = useMemo(() => {
    if (!globalSearchTerm) return favoriteProjects;
    const term = globalSearchTerm.toLowerCase();
    return favoriteProjects.filter(fp => 
      fp.project?.title?.toLowerCase().includes(term) ||
      fp.project?.description?.toLowerCase().includes(term) ||
      fp.project?.city?.toLowerCase().includes(term) ||
      fp.project?.country?.toLowerCase().includes(term) ||
      fp.company?.name?.toLowerCase().includes(term)
    );
  }, [favoriteProjects, globalSearchTerm]);
  
  const filteredCompaniesGlobal = useMemo(() => {
    if (!globalSearchTerm) return favoriteCompanies;
    const term = globalSearchTerm.toLowerCase();
    return favoriteCompanies.filter(fc => 
      fc.company?.name?.toLowerCase().includes(term) ||
      fc.company?.industry?.toLowerCase().includes(term) ||
      fc.company?.city?.toLowerCase().includes(term) ||
      fc.company?.country?.toLowerCase().includes(term)
    );
  }, [favoriteCompanies, globalSearchTerm]);
  
  const filteredPartnersGlobal = useMemo(() => {
    if (!globalSearchTerm) return businessPartners;
    const term = globalSearchTerm.toLowerCase();
    return businessPartners.filter(bp => 
      bp.company_name?.toLowerCase().includes(term) ||
      bp.company_email?.toLowerCase().includes(term) ||
      bp.notes?.toLowerCase().includes(term)
    );
  }, [businessPartners, globalSearchTerm]);
  
  const filteredResourcesGlobal = useMemo(() => {
    if (!globalSearchTerm) return filteredFavorites;
    const term = globalSearchTerm.toLowerCase();
    return filteredFavorites.filter(fav => {
      if (!fav.resource) return false;
      return (
        fav.resource.name?.toLowerCase().includes(term) ||
        fav.resource.role?.toLowerCase().includes(term) ||
        fav.resource.city?.toLowerCase().includes(term) ||
        fav.resource.country?.toLowerCase().includes(term) ||
        fav.company?.name?.toLowerCase().includes(term)
      );
    });
  }, [filteredFavorites, globalSearchTerm]);

  const handleUpdateTags = async (favoriteId, newTags) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/favorites/${favoriteId}/tags`, 
        { tags: newTags }, // Backend expects {tags: [...]}
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        }
      );
      toast.success('Tags updated successfully');
      setEditingTags(null);
      fetchFavorites();
    } catch (error) {
      toast.error('Failed to update tags');
      console.error(error);
    }
  };

  const handleUpdateNotes = async (favoriteId, notes) => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/favorites/${favoriteId}/notes`, 
        { private_notes: notes }, // Backend expects {private_notes: "..."}
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        }
      );
      toast.success('Notes updated successfully');
      setEditingNotes(null);
      fetchFavorites();
    } catch (error) {
      toast.error('Failed to update notes');
      console.error(error);
    }
  };

  const handleRemoveFavorite = async (resourceId) => {
    if (!window.confirm('Remove this resource from favorites?')) return;
    
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/favorites/resource/${resourceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resource removed from favorites');
      fetchFavorites();
    } catch (error) {
      toast.error('Failed to remove favorite');
    }
  };

  const handleContact = (resource, company) => {
    // Save current state before navigating
    navigateWithState(navigate, '/messages', {
      path: '/favorites',
      searchTerm,
      filterTag,
      sortBy,
      pageName: 'Favorites',
      locationState: { 
        targetCompanyId: company?.id,
        targetCompanyName: company?.name,
        resourceInfo: {
          id: resource.id,
          name: resource.name,
          role: resource.role
        }
      }
    });
  };

  // Bulk selection handlers
  const toggleResourceSelection = (favorite) => {
    const resourceData = {
      id: favorite.resource.id,
      name: favorite.resource.name,
      role: favorite.resource.role,
      companyId: favorite.company.id,
      companyName: favorite.company.name
    };

    setSelectedResources(prev => {
      const isSelected = prev.some(r => r.id === favorite.resource.id);
      if (isSelected) {
        return prev.filter(r => r.id !== favorite.resource.id);
      } else {
        return [...prev, resourceData];
      }
    });
  };

  const clearSelection = () => {
    setSelectedResources([]);
  };

  const isResourceSelected = (resourceId) => {
    return selectedResources.some(r => r.id === resourceId);
  };

  const fetchMyProjects = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/projects/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const handlePreReserve = (resourceId) => {
    if (myProjects.length === 0) {
      toast.error('You need to create a project first before pre-reserving resources');
      return;
    }
    
    if (myProjects.length === 1) {
      // If only one project, use it directly
      preReserveResource(myProjects[0].id, resourceId);
    } else {
      // Show project selector
      setShowProjectSelector(resourceId);
    }
  };

  const preReserveResource = (projectId, resourceId) => {
    // Find project and resource details
    const project = myProjects.find(p => p.id === projectId);
    const favorite = favorites.find(fav => fav.resource.id === resourceId);
    
    setShowProjectSelector(null); // Close project selector
    setShowPreReservationModal({
      projectId,
      resourceId,
      resourceName: favorite?.resource?.name || 'Resource',
      projectTitle: project?.title || 'Project'
    });
  };

  const handleConfirmPreReservation = async (customMessage) => {
    if (!showPreReservationModal) return;
    
    const { projectId, resourceId } = showPreReservationModal;
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(`${API}/pre-reservations`, null, {
        params: { 
          project_id: projectId, 
          resource_id: resourceId,
          custom_message: customMessage || undefined
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pre-reservation request sent');
      setShowPreReservationModal(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send pre-reservation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#62D84E' }}></div>
      </div>
    );
  }

  // RM-specific rendering
  if (isRM) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>
            <Star className="inline w-8 h-8 mr-2 text-yellow-500" />
            My Favorites
          </h1>
          <p className="text-gray-600 mt-2">
            Track interesting projects, companies and partners
          </p>
        </div>

        {/* Global Search Bar */}
        <Card className="mb-6 border-2 border-gray-100">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search across all favorites (projects, companies, partners)..."
                value={globalSearchTerm}
                onChange={(e) => setGlobalSearchTerm(e.target.value)}
                className="pl-11 py-5 text-base"
                data-testid="global-favorites-search"
              />
              {globalSearchTerm && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute right-2 top-2"
                  onClick={() => setGlobalSearchTerm('')}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            {globalSearchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                Showing results for &ldquo;{globalSearchTerm}&rdquo; - 
                {filteredProjectsGlobal.length} projects, 
                {filteredCompaniesGlobal.length} companies, 
                {filteredPartnersGlobal.length} partners
              </p>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">Loading favorites...</div>
        ) : (
          <div className="space-y-4">
            {/* Favorite Projects Section - Collapsible */}
            <Collapsible open={expandedSections.projects} onOpenChange={() => toggleSection('projects')}>
              <Card className="border-l-4 border-l-yellow-400">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        {expandedSections.projects ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                        <Star className="w-5 h-5 text-yellow-500" />
                        Favorite Projects 
                        <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                          {filteredProjectsGlobal.length}
                        </span>
                      </CardTitle>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 ml-7">
                      Projects you&apos;ve bookmarked to apply or follow up on
                    </p>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                    {filteredProjectsGlobal.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Star className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>{globalSearchTerm ? 'No projects match your search' : 'No favorite projects yet'}</p>
                        {!globalSearchTerm && <p className="text-sm mt-2">Browse projects in the Matching page and add them to favorites</p>}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredProjectsGlobal.map((favProject) => (
                          <Card key={favProject.favorite?.id || favProject.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start mb-2">
                                <h3 className="font-semibold text-lg">{favProject.project?.title || 'Project'}</h3>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const projectId = favProject.favorite?.project_id || favProject.project?.id;
                                      await axios.delete(`${API}/favorites/project/${projectId}`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      setFavoriteProjects(prev => prev.filter(p => 
                                        (p.favorite?.project_id || p.project?.id) !== projectId
                                      ));
                                      toast.success('Removed from favorites');
                                    } catch (error) {
                                      toast.error('Failed to remove from favorites');
                                    }
                                  }}
                                >
                                  <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                                </Button>
                              </div>
                              <p className="text-sm text-gray-600 mb-2 line-clamp-2">{favProject.project?.description}</p>
                              <div className="flex gap-2 text-sm text-gray-500 mb-3">
                                <span 
                                  className="hover:text-blue-600 hover:underline cursor-pointer"
                                  onClick={() => navigate(`/company/${favProject.company?.id}`)}
                                >
                                  Company: {favProject.company?.name}
                                </span>
                              </div>
                              <Button
                                className="w-full btn-snow"
                                onClick={() => {
                                  const projectId = favProject.favorite?.project_id || favProject.project?.id;
                                  navigateWithState(navigate, `/projects/${projectId}`, {
                                    path: '/favorites',
                                    scrollPosition: window.scrollY,
                                    pageName: 'Favorites'
                                  });
                                }}
                              >
                                View Details
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Favorite Companies Section - Collapsible */}
            <Collapsible open={expandedSections.companies} onOpenChange={() => toggleSection('companies')}>
              <Card className="border-l-4 border-l-blue-400">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        {expandedSections.companies ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                        <Building2 className="w-5 h-5 text-blue-500" />
                        Favorite Companies 
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-sm rounded-full">
                          {filteredCompaniesGlobal.length}
                        </span>
                      </CardTitle>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 ml-7">
                      Companies you follow to stay updated on their new projects
                    </p>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                    {filteredCompaniesGlobal.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>{globalSearchTerm ? 'No companies match your search' : 'No favorite companies yet'}</p>
                        {!globalSearchTerm && <p className="text-sm mt-2">Follow companies to get notified about their new projects</p>}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {filteredCompaniesGlobal.map((favCompany) => (
                          <Card key={favCompany.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4 text-center">
                              <div className="flex justify-end mb-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    toast.success('Unfollowed company');
                                  }}
                                >
                                  <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                                </Button>
                              </div>
                              <h3 className="font-semibold text-lg mb-2">{favCompany.company?.name}</h3>
                              <p className="text-sm text-gray-600 mb-3">{favCompany.company?.industry}</p>
                              <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => navigate(`/companies/${favCompany.company_id}`)}
                              >
                                View Profile
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Business Partners Section - Collapsible */}
            <Collapsible open={expandedSections.partners} onOpenChange={() => toggleSection('partners')}>
              <Card className="border-l-4 border-l-purple-400">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        {expandedSections.partners ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                        <Handshake className="w-5 h-5 text-purple-500" />
                        Business Partners 
                        <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 text-sm rounded-full">
                          {filteredPartnersGlobal.length}
                        </span>
                      </CardTitle>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAddPartnerModal(true);
                        }}
                        size="sm"
                        style={{ backgroundColor: '#62D84E' }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Partner
                      </Button>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 ml-7">
                      Companies you have established business relationships with
                    </p>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                    {filteredPartnersGlobal.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Handshake className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p>{globalSearchTerm ? 'No partners match your search' : 'No business partners added yet'}</p>
                        {!globalSearchTerm && <p className="text-sm mt-1">Add companies you regularly work with to quickly filter matches</p>}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredPartnersGlobal.map((partner) => (
                          <Card key={partner.id} className="border border-purple-100 hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                    <Building2 className="w-5 h-5 text-purple-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{partner.company_name}</p>
                                    {partner.company_email && (
                                      <p className="text-xs text-gray-500">{partner.company_email}</p>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeBusinessPartner(partner.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              {partner.notes && (
                                <p className="text-sm text-gray-600 mt-2 pl-13">{partner.notes}</p>
                              )}
                              <p className="text-xs text-gray-400 mt-2">
                                Added {new Date(partner.created_at).toLocaleDateString()}
                              </p>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </div>
        )}
        
        {/* Add Business Partner Modal */}
        <Dialog open={showAddPartnerModal} onOpenChange={setShowAddPartnerModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Handshake className="w-5 h-5 text-purple-500" />
                Add Business Partner
              </DialogTitle>
              <DialogDescription>
                Search for registered companies or add a partner manually.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Search existing companies */}
              <div>
                <Label>Search Registered Companies</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by company name..."
                    value={partnerSearchTerm}
                    onChange={(e) => {
                      setPartnerSearchTerm(e.target.value);
                      searchCompaniesForPartner(e.target.value);
                    }}
                    className="pl-10"
                  />
                </div>
                
                {searchingCompanies && (
                  <p className="text-sm text-gray-500 mt-2">Searching...</p>
                )}
                {searchedCompanies.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                    {searchedCompanies.map((company) => (
                      <button
                        key={company.id}
                        onClick={() => addBusinessPartner(company)}
                        className="w-full text-left p-3 hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-3"
                      >
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="font-medium">{company.name}</p>
                          <p className="text-xs text-gray-500">{company.contact_email || company.country || 'No details'}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="relative flex items-center">
                <div className="flex-grow border-t border-gray-300"></div>
                <span className="flex-shrink mx-4 text-gray-400 text-sm">or add manually</span>
                <div className="flex-grow border-t border-gray-300"></div>
              </div>
              
              {/* Manual entry */}
              <div>
                <Label>Company Name *</Label>
                <Input
                  placeholder="Enter company name"
                  value={manualPartnerName}
                  onChange={(e) => setManualPartnerName(e.target.value)}
                />
              </div>
              
              <div>
                <Label>Contact Email (optional)</Label>
                <Input
                  type="email"
                  placeholder="contact@company.com"
                  value={manualPartnerEmail}
                  onChange={(e) => setManualPartnerEmail(e.target.value)}
                />
              </div>
              
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Add notes about this partnership..."
                  value={partnerNotes}
                  onChange={(e) => setPartnerNotes(e.target.value)}
                  rows={2}
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddPartnerModal(false);
                    resetPartnerForm();
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => addBusinessPartner({ name: manualPartnerName })}
                  disabled={!manualPartnerName}
                  className="flex-1"
                  style={{ backgroundColor: '#62D84E' }}
                >
                  Add Partner
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // PM rendering with collapsible sections
  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>
          <Star className="inline w-8 h-8 mr-2 text-yellow-500" />
          My Favorites
        </h1>
        <p className="text-gray-600 mt-2">
          Track interesting resources, companies and partners
        </p>
      </div>

      {/* Global Search Bar */}
      <Card className="mb-6 border-2 border-gray-100">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search across all favorites (resources, companies, partners)..."
              value={globalSearchTerm}
              onChange={(e) => setGlobalSearchTerm(e.target.value)}
              className="pl-11 py-5 text-base"
              data-testid="global-favorites-search-pm"
            />
            {globalSearchTerm && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="absolute right-2 top-2"
                onClick={() => setGlobalSearchTerm('')}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          {globalSearchTerm && (
            <p className="text-sm text-gray-500 mt-2">
              Showing results for &ldquo;{globalSearchTerm}&rdquo; - 
              {filteredResourcesGlobal.length} resources, 
              {filteredCompaniesGlobal.length} companies, 
              {filteredPartnersGlobal.length} partners
            </p>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {/* Favorite Resources Section - Collapsible */}
        <Collapsible open={expandedSections.resources} onOpenChange={() => toggleSection('resources')}>
          <Card className="border-l-4 border-l-yellow-400">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {expandedSections.resources ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                    <Star className="w-5 h-5 text-yellow-500" />
                    Favorite Resources
                    <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                      {filteredResourcesGlobal.length}
                    </span>
                  </CardTitle>
                </div>
                <p className="text-sm text-gray-500 mt-1 ml-7">
                  Consultants you&apos;ve bookmarked for your projects
                </p>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                {/* Filters inside the collapsible */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Search */}
                    <div>
                      <Label>Search Resources</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search by name, role, company..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Filter by Tag */}
                    <div>
                      <Label>Filter by Tag</Label>
                      <select
                        value={filterTag}
                        onChange={(e) => setFilterTag(e.target.value)}
                        className="w-full border rounded-md px-3 py-2"
                      >
                        <option value="">All Tags</option>
                        {allTags.map(tag => (
                          <option key={tag} value={tag}>{tag}</option>
                        ))}
                      </select>
                    </div>

                    {/* Sort */}
                    <div>
                      <Label>Sort By</Label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="w-full border rounded-md px-3 py-2"
                      >
                        <option value="created_at">Date Added (Newest)</option>
                        <option value="name">Name (A-Z)</option>
                        <option value="rate">Daily Rate (High to Low)</option>
                        <option value="tags">Most Tagged</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Favorites List */}
                {filteredResourcesGlobal.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Star className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{globalSearchTerm || searchTerm ? 'No resources match your search' : 'No favorite resources yet'}</p>
                    {!globalSearchTerm && !searchTerm && (
                      <p className="text-sm mt-2">Browse resources and click the star icon to add them to your favorites</p>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4">
                    {filteredResourcesGlobal.map(({ favorite, resource, company }) => (
                      <Card key={favorite.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Checkbox for selection */}
                            <button
                              onClick={() => toggleResourceSelection({ favorite, resource, company })}
                              className="mt-1 flex-shrink-0 hover:opacity-70 transition-opacity"
                            >
                              {isResourceSelected(resource.id) ? (
                                <CheckSquare className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400" />
                              )}
                            </button>

                            {/* Resource Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <h3 className="text-lg font-semibold" style={{ color: '#1B3240' }}>
                                  {resource.name}
                                </h3>
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  {resource.role}
                                </span>
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-800 text-xs rounded-full">
                                  {resource.seniority}
                                </span>
                              </div>
                              
                              <p className="text-sm text-gray-600 mb-2">
                                <button
                                  onClick={() => navigateWithState(navigate, `/company/${company?.id}`, {
                                    path: '/favorites',
                                    searchTerm,
                                    filterTag,
                                    sortBy,
                                    pageName: 'Favorites'
                                  })}
                                  className="hover:text-blue-600 hover:underline font-medium"
                                >
                                  {company?.name || 'Unknown Company'}
                                </button> • {resource.city}, {resource.country}
                              </p>

                              <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
                                <span className="font-semibold text-green-600">
                                  {resource.currency} {resource.daily_rate}/day
                                </span>
                                <span>•</span>
                                <span>{resource.availability_status}</span>
                              </div>

                              {/* Tags - compact */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <Tag className="w-3 h-3 text-gray-400" />
                                {favorite.tags && favorite.tags.length > 0 ? (
                                  <>
                                    {favorite.tags.map((tag, idx) => (
                                      <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full">
                                        {tag}
                                      </span>
                                    ))}
                                    <button
                                      onClick={() => setEditingTags(favorite.id)}
                                      className="text-xs text-blue-600 hover:underline"
                                    >
                                      Edit
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => setEditingTags(favorite.id)}
                                    className="text-xs text-blue-600 hover:underline"
                                  >
                                    + Add tags
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Actions - compact */}
                            <div className="flex flex-col gap-1">
                              <ViewResourceProfileButton 
                                resourceId={resource.id}
                                variant="outline"
                                size="sm"
                                navigationState={{
                                  path: '/favorites',
                                  searchTerm,
                                  filterTag,
                                  sortBy,
                                  pageName: 'Favorites'
                                }}
                              />
                              <Button
                                onClick={() => handlePreReserve(resource.id)}
                                style={{ backgroundColor: '#62D84E' }}
                                size="sm"
                              >
                                <Send className="w-3 h-3 mr-1" />
                                Pre-Reserve
                              </Button>
                              <Button
                                onClick={() => handleContact(resource, company)}
                                variant="outline"
                                size="sm"
                              >
                                <MessageSquare className="w-3 h-3 mr-1" />
                                Contact
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => handleRemoveFavorite(resource.id)}
                                className="text-red-600"
                                size="sm"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Remove
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Favorite Companies Section - Collapsible */}
        <Collapsible open={expandedSections.companies} onOpenChange={() => toggleSection('companies')}>
          <Card className="border-l-4 border-l-blue-400">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {expandedSections.companies ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                    <Building2 className="w-5 h-5 text-blue-500" />
                    Favorite Companies
                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-sm rounded-full">
                      {filteredCompaniesGlobal.length}
                    </span>
                  </CardTitle>
                </div>
                <p className="text-sm text-gray-500 mt-1 ml-7">
                  Companies you follow for new consultant availability
                </p>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                {filteredCompaniesGlobal.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{globalSearchTerm ? 'No companies match your search' : 'No favorite companies yet'}</p>
                    {!globalSearchTerm && <p className="text-sm mt-2">Follow companies to get notified about their new projects</p>}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {filteredCompaniesGlobal.map((favCompany) => (
                      <Card key={favCompany.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4 text-center">
                          <div className="flex justify-end mb-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                toast.success('Unfollowed company');
                              }}
                            >
                              <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                            </Button>
                          </div>
                          <h3 className="font-semibold text-lg mb-2">{favCompany.company?.name}</h3>
                          <p className="text-sm text-gray-600 mb-3">{favCompany.company?.industry}</p>
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => navigate(`/companies/${favCompany.company_id}`)}
                          >
                            View Profile
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>

        {/* Business Partners Section - Collapsible */}
        <Collapsible open={expandedSections.partners} onOpenChange={() => toggleSection('partners')}>
          <Card className="border-l-4 border-l-purple-400">
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {expandedSections.partners ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                    <Handshake className="w-5 h-5 text-purple-500" />
                    Business Partners
                    <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 text-sm rounded-full">
                      {filteredPartnersGlobal.length}
                    </span>
                  </CardTitle>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAddPartnerModal(true);
                    }}
                    size="sm"
                    style={{ backgroundColor: '#62D84E' }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Partner
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1 ml-7">
                  Companies you have established business relationships with
                </p>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                {filteredPartnersGlobal.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Handshake className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>{globalSearchTerm ? 'No partners match your search' : 'No business partners added yet'}</p>
                    {!globalSearchTerm && <p className="text-sm mt-1">Add companies you regularly work with to quickly filter matches</p>}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPartnersGlobal.map((partner) => (
                      <Card key={partner.id} className="border border-purple-100 hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{partner.company_name}</p>
                                {partner.company_email && (
                                  <p className="text-xs text-gray-500">{partner.company_email}</p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeBusinessPartner(partner.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {partner.notes && (
                            <p className="text-sm text-gray-600 mt-2 pl-13">{partner.notes}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-2">
                            Added {new Date(partner.created_at).toLocaleDateString()}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>

      {/* Add Business Partner Modal */}
      <Dialog open={showAddPartnerModal} onOpenChange={setShowAddPartnerModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="w-5 h-5 text-purple-500" />
              Add Business Partner
            </DialogTitle>
            <DialogDescription>
              Search for registered companies or add a partner manually.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {/* Search existing companies */}
            <div>
              <Label>Search Registered Companies</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by company name..."
                  value={partnerSearchTerm}
                  onChange={(e) => {
                    setPartnerSearchTerm(e.target.value);
                    searchCompaniesForPartner(e.target.value);
                  }}
                  className="pl-10"
                />
              </div>
              
              {/* Search results */}
              {searchingCompanies && (
                <p className="text-sm text-gray-500 mt-2">Searching...</p>
              )}
              {searchedCompanies.length > 0 && (
                <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                  {searchedCompanies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => addBusinessPartner(company)}
                      className="w-full text-left p-3 hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-3"
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <Building2 className="w-4 h-4 text-gray-500" />
                      </div>
                      <div>
                        <p className="font-medium">{company.name}</p>
                        <p className="text-xs text-gray-500">{company.contact_email || company.country || 'No details'}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="relative flex items-center">
              <div className="flex-grow border-t border-gray-300"></div>
              <span className="flex-shrink mx-4 text-gray-400 text-sm">or add manually</span>
              <div className="flex-grow border-t border-gray-300"></div>
            </div>
            
            {/* Manual entry */}
            <div>
              <Label>Company Name *</Label>
              <Input
                placeholder="Enter company name"
                value={manualPartnerName}
                onChange={(e) => setManualPartnerName(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Contact Email (optional)</Label>
              <Input
                type="email"
                placeholder="contact@company.com"
                value={manualPartnerEmail}
                onChange={(e) => setManualPartnerEmail(e.target.value)}
              />
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Add notes about this partnership..."
                value={partnerNotes}
                onChange={(e) => setPartnerNotes(e.target.value)}
                rows={2}
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddPartnerModal(false);
                  resetPartnerForm();
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => addBusinessPartner({ name: manualPartnerName })}
                disabled={!manualPartnerName}
                className="flex-1"
                style={{ backgroundColor: '#62D84E' }}
              >
                Add Partner
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tags Editing Modal */}
      {editingTags && (
        <Dialog open={!!editingTags} onOpenChange={() => setEditingTags(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Tags</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Input
                placeholder="Add tags separated by commas"
                defaultValue={filteredFavorites.find(f => f.favorite.id === editingTags)?.favorite.tags?.join(', ')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const tags = e.target.value.split(',').map(t => t.trim()).filter(t => t);
                    handleUpdateTags(editingTags, tags);
                  }
                }}
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditingTags(null)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={(e) => {
                    const input = e.target.closest('.space-y-4').querySelector('input');
                    const tags = input.value.split(',').map(t => t.trim()).filter(t => t);
                    handleUpdateTags(editingTags, tags);
                  }}
                  className="flex-1"
                  style={{ backgroundColor: '#62D84E' }}
                >
                  Save Tags
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Project Selector Modal */}
      {showProjectSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4" style={{ color: '#1B3240' }}>
              Select a Project
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose which project you want to pre-reserve this resource for:
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {myProjects.map(project => (
                <button
                  key={project.id}
                  onClick={() => preReserveResource(project.id, showProjectSelector)}
                  className="w-full text-left p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="font-medium">{project.title}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {project.project_id} • {project.city}, {project.country}
                  </div>
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowProjectSelector(null)}
              className="w-full mt-4"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Contact Bar */}
      <BulkContactBar
        selectedItems={selectedResources}
        onClearSelection={clearSelection}
        itemType="resources"
      />

      {/* Pre-Reservation Modal */}
      <PreReservationModal
        isOpen={!!showPreReservationModal}
        onClose={() => setShowPreReservationModal(null)}
        onConfirm={handleConfirmPreReservation}
        resourceName={showPreReservationModal?.resourceName}
        projectTitle={showPreReservationModal?.projectTitle}
      />
    </div>
  );
};

export default FavoritesPage;
